import React, { useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { MoreVertical, Archive, Trash2, FolderInput, RotateCcw, Trash, Tag } from 'lucide-react';
import MonthGroupHeader from './MonthGroupHeader';
import { groupByMonth } from './mediaHelpers';
import { CONTENT_CATEGORIES } from '../../utils/contentCategories';

/**
 * Wrap a rendered media card with dnd-kit draggable + hover menu overlay.
 * `children` is the pre-rendered card (output of renderMediaCard).
 */
const DraggableCard = ({ item, view, selected, onMenu, menuOpen, onMenuAction, children }) => {
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  React.useEffect(() => {
    if (!menuOpen) setShowCategoryPicker(false);
  }, [menuOpen]);
  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: `media:${item.contentId || item.id}`,
    data: { type: 'media', contentId: item.contentId || item.id, item },
  });
  const style = {
    position: 'relative',
    opacity: isDragging ? 0.4 : 1,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    cursor: isDragging ? 'grabbing' : 'grab',
    touchAction: 'none',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
      <button
        type="button"
        className="mv2-card-menu-trigger"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onMenu(item);
        }}
        aria-label="Open actions menu"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <MoreVertical size={16} />
      </button>
      {menuOpen && (
        <div
          className="mv2-card-menu"
          role="menu"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {view === 'active' && !showCategoryPicker && (
            <>
              <button type="button" role="menuitem" onClick={() => setShowCategoryPicker(true)}>
                <Tag size={14} /> Set category…
              </button>
              <button type="button" role="menuitem" onClick={() => onMenuAction('archive', item)}>
                <Archive size={14} /> Archive
              </button>
              <button type="button" role="menuitem" onClick={() => onMenuAction('trash', item)}>
                <Trash2 size={14} /> Move to Trash
              </button>
              <button type="button" role="menuitem" onClick={() => onMenuAction('move', item)}>
                <FolderInput size={14} /> Move to Folder…
              </button>
            </>
          )}
          {view === 'active' && showCategoryPicker && (
            <>
              {CONTENT_CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  role="menuitem"
                  onClick={() => onMenuAction('setCategory', item, c.key)}
                  style={item.category === c.key ? { fontWeight: 700 } : undefined}
                >
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: c.color, display: 'inline-block', marginRight: 6,
                  }} />
                  {c.label}
                </button>
              ))}
              <button
                type="button"
                role="menuitem"
                onClick={() => onMenuAction('setCategory', item, null)}
                style={!item.category ? { fontWeight: 700 } : undefined}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: '#9ca3af', display: 'inline-block', marginRight: 6,
                }} />
                Uncategorized
              </button>
            </>
          )}
          {view === 'archived' && (
            <>
              <button type="button" role="menuitem" onClick={() => onMenuAction('unarchive', item)}>
                <RotateCcw size={14} /> Unarchive
              </button>
              <button type="button" role="menuitem" onClick={() => onMenuAction('trash', item)}>
                <Trash2 size={14} /> Move to Trash
              </button>
            </>
          )}
          {view === 'trash' && (
            <>
              <button type="button" role="menuitem" onClick={() => onMenuAction('restore', item)}>
                <RotateCcw size={14} /> Restore
              </button>
              <button type="button" role="menuitem" onClick={() => onMenuAction('purge', item)} className="mv2-card-menu__danger">
                <Trash size={14} /> Delete Forever
              </button>
            </>
          )}
        </div>
      )}
      {selected && <span className="mv2-card-selected-ring" aria-hidden="true" />}
    </div>
  );
};

/**
 * MediaGrid — month-grouped, DnD-enabled grid.
 *
 * Props:
 *   items: normalized media items
 *   view: 'active' | 'archived' | 'trash'
 *   renderCard: (item, options) => ReactNode — reuses legacy renderMediaCard
 *   selectedIds: Set<string>  (item.id)
 *   onToggleSelect(itemId)
 *   onSelectMonth(ids, selectAll)
 *   onSchedule(item)
 *   onMenuAction(action, item)
 *   hoveredCard, setHoveredCard, videoPreviewRef, scheduleByContentId
 */
const MediaGrid = ({
  items,
  view,
  renderCard,
  selectedIds,
  onToggleSelect,
  onSelectMonth,
  onSchedule,
  onMenuAction,
  hoveredCard,
  setHoveredCard,
  videoPreviewRef,
  scheduleByContentId,
}) => {
  const [openMenuId, setOpenMenuId] = useState(null);

  const handleMenu = (item) => {
    setOpenMenuId((cur) => (cur === item.id ? null : item.id));
  };

  React.useEffect(() => {
    if (!openMenuId) return undefined;
    const close = () => setOpenMenuId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openMenuId]);

  const groups = groupByMonth(items);

  if (items.length === 0) {
    return (
      <div className="media__empty">
        <h3 className="media__empty-title">
          {view === 'archived'
            ? 'No archived media'
            : view === 'trash'
              ? 'Trash is empty'
              : 'No media here yet'}
        </h3>
        <p className="media__empty-body">
          {view === 'active'
            ? 'Upload assets or drop them into a folder to get started.'
            : view === 'archived'
              ? 'Archived media will appear here — safe, not scheduled.'
              : 'Trashed media will appear here until you purge or restore.'}
        </p>
      </div>
    );
  }

  return (
    <div className="mv2-grid-root">
      {groups.map((group) => {
        const ids = group.items.map((it) => it.id);
        const selectedInMonth = ids.filter((id) => selectedIds.has(id));
        const allSelected = selectedInMonth.length === ids.length && ids.length > 0;
        const someSelected = selectedInMonth.length > 0 && !allSelected;
        return (
          <section key={group.key} className="mv2-grid-section">
            <MonthGroupHeader
              label={group.label}
              count={group.items.length}
              allSelected={allSelected}
              someSelected={someSelected}
              onToggle={() => onSelectMonth(ids, !allSelected)}
            />
            <div className="media__grid">
              {group.items.map((item) => (
                <DraggableCard
                  key={item.id}
                  item={item}
                  view={view}
                  selected={selectedIds.has(item.id)}
                  onMenu={handleMenu}
                  menuOpen={openMenuId === item.id}
                  onMenuAction={(action, it, arg) => {
                    setOpenMenuId(null);
                    onMenuAction(action, it, arg);
                  }}
                >
                  {renderCard(item, {
                    isSelected: selectedIds.has(item.id),
                    isHovered: hoveredCard === item.id,
                    isLive: !!(item.contentId && scheduleByContentId?.[item.contentId]),
                    onEnter: () => setHoveredCard(item.id),
                    onLeave: () => {
                      if (videoPreviewRef?.current) videoPreviewRef.current.pause();
                      setHoveredCard(null);
                    },
                    onToggle: () => onToggleSelect(item.id),
                    onSchedule: (e) => {
                      e.stopPropagation();
                      onSchedule(item);
                    },
                    videoPreviewRef,
                    keyPrefix: `mv2-${view}-`,
                  })}
                </DraggableCard>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default MediaGrid;
